import {
  STORE_ORDER_SUMMARY_EST_DELIVERY_MAX_DAYS_AFTER_CLOSE,
  STORE_ORDER_SUMMARY_EST_DELIVERY_MIN_DAYS_AFTER_CLOSE,
} from '../constants/storeCheckout.constants';
import { convertStoreCheckoutCalendarDateToUtcIsoString } from './storeCheckoutDate.utils';
import { findStoreCheckoutTimelineElement } from './storeCheckoutShippingTimeline.utils';
import type { ILaunchStoreRequest } from '@/common/interfaces/store.interface';
import type { ITimelineElement } from '@/common/interfaces/cart.interface';
import type { ViewPort } from '@/common/context/ViewPort/ViewPort';
import {
  DESKTOP_VIEW_PORT,
  MOBILE_VIEW_PORT,
  TAB_VIEW_PORT,
} from '@/common/context/ViewPort/ViewPort';
import { parseLocalDateOnly } from '@/utilities/DateUtils';

export type StoreLaunchDeviceType = 'desktop' | 'mobile' | 'tablet';

export interface IBuildStoreLaunchPayloadInput {
  storeId: number;
  orderUntil: string;
  shipTypeKey: string;
  timelineData: ITimelineElement[];
  viewport: ViewPort;
}

/** Maps extensive viewport breakpoints to API-accepted device_type values. */
export const resolveStoreLaunchDeviceType = (viewport: ViewPort): StoreLaunchDeviceType => {
  if (MOBILE_VIEW_PORT.includes(viewport)) {
    return 'mobile';
  }

  if (TAB_VIEW_PORT.includes(viewport)) {
    return 'tablet';
  }

  if (DESKTOP_VIEW_PORT.includes(viewport)) {
    return 'desktop';
  }

  return 'desktop';
};

const addDaysToCalendarDateIso = (calendarDate: string, days: number): string => {
  const parsedCloseDate = parseLocalDateOnly(calendarDate.trim());

  if (!parsedCloseDate) {
    return '';
  }

  const nextDate = new Date(parsedCloseDate);
  nextDate.setDate(nextDate.getDate() + days);

  const year = nextDate.getFullYear();
  const month = String(nextDate.getMonth() + 1).padStart(2, '0');
  const day = String(nextDate.getDate()).padStart(2, '0');

  return convertStoreCheckoutCalendarDateToUtcIsoString(`${year}-${month}-${day}`);
};

export const buildStoreLaunchPayload = ({
  storeId,
  orderUntil,
  shipTypeKey,
  timelineData,
  viewport,
}: IBuildStoreLaunchPayloadInput): ILaunchStoreRequest => {
  const timelineElement = findStoreCheckoutTimelineElement(timelineData, shipTypeKey);

  let fromDeliveryDate = timelineElement?.newTimeline?.minDate?.dateInISOString ?? '';
  let toDeliveryDate = timelineElement?.newTimeline?.maxDate?.dateInISOString ?? '';

  if (!fromDeliveryDate || !toDeliveryDate) {
    fromDeliveryDate = addDaysToCalendarDateIso(
      orderUntil,
      STORE_ORDER_SUMMARY_EST_DELIVERY_MIN_DAYS_AFTER_CLOSE
    );
    toDeliveryDate = addDaysToCalendarDateIso(
      orderUntil,
      STORE_ORDER_SUMMARY_EST_DELIVERY_MAX_DAYS_AFTER_CLOSE
    );
  }

  return {
    order_id: storeId,
    device_type: resolveStoreLaunchDeviceType(viewport),
    // v4-web launchStore: orderDueDate = newTimeline.maxDate.dateInISOString
    order_due_date: toDeliveryDate,
    from_delivery_date: fromDeliveryDate,
    to_delivery_date: toDeliveryDate,
  };
};
